/**
 * Les documents contractuels que le client doit accepter à l'inscription.
 *
 * CE MODULE NE RÉSOUT PLUS QUE DEUX CHOSES
 * ----------------------------------------
 * L'identité des documents, et la langue dans laquelle les servir. Le texte
 * lui-même vit dans `content/`, embarqué dans l'application.
 *
 * Il y avait ici un `legalDocumentUrl()` qui construisait
 * `/legal/Terms_of_Service_FR.docx`. Il a disparu avec le bouton de
 * téléchargement : plus aucun fichier n'est servi ni proposé. Un consentement
 * se lit là où on le donne — renvoyer vers un .docx qui s'ouvre dans Word, et
 * nulle part sur mobile, revient à demander de cocher sans avoir lu.
 *
 * LA VERSION N'EST PAS DÉCORATIVE
 * -------------------------------
 * `LEGAL_DOCUMENT_VERSIONS` est la date d'entrée en vigueur imprimée en tête
 * des documents (« Effective Date: August, 23 2026 »). Elle est dupliquée côté
 * API dans `config/legal.php`, et c'est *l'API* qui fait foi au moment d'écrire
 * la preuve de consentement : ce qui est déclaré ici sert à l'affichage, pas à
 * l'enregistrement. Les deux valeurs doivent bouger ensemble le jour d'une
 * révision — sans quoi l'écran annonce une version que la base ne connaît pas.
 *
 * LANGUES
 * -------
 * Les documents existent en 7 langues, l'interface en 6 : l'estonien est
 * fourni parce que l'éditeur (CodeNekt For Fleet OÜ) est estonien et que le
 * document peut être réclamé dans cette langue, même si aucun écran n'est
 * traduit en estonien. Toute langue non couverte retombe sur l'anglais, qui
 * est de toute façon la version qui prévaut juridiquement.
 */

export type LegalDocumentId = "terms" | "dpa";

/** Date d'entrée en vigueur des deux textes actuellement en ligne. */
export const LEGAL_DOCUMENT_VERSIONS: Record<LegalDocumentId, string> = {
  terms: "2026-08-23",
  dpa: "2026-08-23",
};

/**
 * Langues dans lesquelles le document existe réellement.
 *
 * Ne rien ajouter ici sans le fichier correspondant dans `content/` : une
 * entrée sans texte produit une page vide au moment précis où l'utilisateur
 * vérifie ce qu'il signe. La liste est doublée par `DOCUMENT_LANGUAGES` dans
 * `content/index.ts`, qui pilote le menu de langue et la table d'imports —
 * c'est cette dernière que le compilateur vérifie.
 */
const AVAILABLE_LANGUAGES = ["de", "en", "es", "et", "fr", "it", "pt"] as const;

/** L'anglais prévaut en cas de divergence — c'est écrit dans les documents. */
export const CONTROLLING_LANGUAGE = "en";

/**
 * `fr-FR`, `pt-BR`, `EN` -> code ISO 639-1 en minuscules.
 * i18next expose la langue négociée telle quelle, variantes régionales
 * comprises ; on ne publie qu'un document par langue.
 */
const normalizeLanguage = (language?: string): string =>
  (language || "").toLowerCase().split(/[-_]/)[0];

/** Langue effectivement servie pour cette langue d'interface. */
export const resolveDocumentLanguage = (language?: string): string => {
  const base = normalizeLanguage(language);
  return (AVAILABLE_LANGUAGES as readonly string[]).includes(base)
    ? base
    : CONTROLLING_LANGUAGE;
};

/**
 * Chemin de l'ÉCRAN qui affiche le document.
 *
 * C'est ce que les cases à cocher du formulaire d'inscription visent.
 *
 * Sans paramètre de langue : la page part de la locale i18next courante et
 * offre un menu pour en changer. Mettre la langue dans l'URL créerait deux
 * sources de vérité, et un lien partagé figerait la langue de celui qui l'a
 * copié.
 */
export const LEGAL_DOCUMENT_PATHS: Record<LegalDocumentId, string> = {
  terms: "/legal/terms",
  dpa: "/legal/dpa",
};

export const legalDocumentPath = (document: LegalDocumentId): string =>
  LEGAL_DOCUMENT_PATHS[document];

/**
 * Vrai quand le texte affiché n'est qu'une traduction de courtoisie, ce que
 * l'écran doit dire au lieu de le laisser deviner.
 */
export const isTranslatedVersion = (language?: string): boolean =>
  resolveDocumentLanguage(language) !== CONTROLLING_LANGUAGE;
